import React from 'react';

import { ReactComponent as DownArrow } from 'assets/icons/ic_triangle.svg';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Check } from 'lucide-react';
import { cn } from 'shared/lib';

type SearchableDropdownProps = {
  label?: string;
  placeholder?: string;
  error?: string;
  options: string[];
  value: string;
  setValue: (value: string) => void;
  className?: string;
  isDisabled?: boolean;
  initialValue?: string;
  errorFocus?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
};

const SearchableDropdown = ({
  label,
  placeholder = 'Select',
  error,
  options,
  value,
  setValue,
  className = 'w-full',
  isDisabled,

  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
}: SearchableDropdownProps) => {
  const [open, setOpen] = React.useState(false);

  const borderAndBgColor = () => {
    const isActive = value !== '';
    const isError = error !== undefined && error !== '';

    if (isError) return 'border-error bg-white';
    if (isDisabled) return 'border-gray-20 bg-gray-20';
    if (isActive) return 'border-gray-30 bg-white';
    return 'border-gray-20 bg-gray-0';
  };

  return (
    <div className={`relative flex flex-col gap-y-0.5 ${className}`}>
      <div className="relative flex w-full flex-col gap-y-2">
        {label !== undefined && <span className="text-c3">{label}</span>}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild disabled={isDisabled}>
            <button
              role="combobox"
              aria-expanded={open}
              className={cn(
                'flex h-11 w-full cursor-pointer items-center gap-x-1 rounded border p-2.5',
                borderAndBgColor(),
              )}
            >
              <span className={`flex-1 text-left text-c4 ${value !== '' ? 'text-black' : 'text-gray-30'}`}>
                {value || placeholder}
              </span>
              <DownArrow
                width={24}
                height={24}
                className={`${open ? 'rotate-180' : ''} transform text-[#ACACAC] duration-150`}
              />
            </button>
          </PopoverTrigger>

          <PopoverContent
            className="mt-[6px] w-[--radix-popover-trigger-width] border-none p-0 shadow-md"
            align="start"
          >
            <Command className="w-full rounded-md border-none bg-white shadow-md">
              <CommandInput
                placeholder={searchPlaceholder}
                className="h-10 border-none px-4 pl-2.5 pr-3 text-c4 text-gray-50 outline-none placeholder:text-gray-30"
              />
              <CommandEmpty className="p-2 text-c4 text-gray-30">{emptyMessage}</CommandEmpty>
              <CommandGroup className="flex max-h-48 w-full flex-col overflow-y-auto rounded-md border-none bg-white shadow-md">
                {options.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={(currentValue) => {
                      setValue(currentValue);
                      setOpen(false);
                    }}
                    className="cursor-pointer border-none px-4 py-3 text-[13px] leading-[1.5] tracking-[-0.022em] text-gray-50 hover:bg-gray-0"
                  >
                    {option}
                    <Check className={cn('ml-auto h-4 w-4', value === option ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <span className="h-4 text-c4 text-error">{error}</span>
    </div>
  );
};

export default SearchableDropdown;
